import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { SUPPLIER_ROLES, validateSupplierInput, type SupplierInput, type SupplierRole } from "@torre/assets";

import Modal from "../../../components/base/Modal";
import { Input } from "../../../components/ui";
import { useActivosMutation } from "../hooks/useActivos";
import { handleSaveError } from "../lib/conflicts";
import { fieldErrors, optionalText } from "../lib/format";
import { langOf, supplierRoleLabel } from "../lib/vocab";
import { activosApi, type SupplierRow } from "../services/activosApi";
import { FormBlock, FormFooter } from "./shared";

// ============================================================================
// Alta y edición de un proveedor de activos (`asset_suppliers`, no
// `providers`). El RFC es único por compañía y no se edita. Archivo común,
// idéntico en CC-CONSOLE y WAYAK.
// ============================================================================

export function SupplierFormModal({
  supplier,
  onClose,
  onOpenSupplier,
}: {
  supplier?: SupplierRow | null;
  onClose: () => void;
  /** Abre el proveedor que ya tiene ese RFC (409 `PROVEEDOR_DUPLICADO`). */
  onOpenSupplier?: (supplierId: string) => void;
}) {
  const { t, i18n } = useTranslation("activos");
  const lang = langOf(i18n.language);
  const navigate = useNavigate();
  const isEdit = Boolean(supplier?.id);
  const [name, setName] = useState(supplier?.name || "");
  const [rfc, setRfc] = useState(supplier?.rfc || "");
  const [roles, setRoles] = useState<SupplierRole[]>(supplier?.roles?.length ? supplier.roles : ["vende"]);
  const [contactName, setContactName] = useState(supplier?.contact?.name || "");
  const [email, setEmail] = useState(supplier?.contact?.email || "");
  const [phone, setPhone] = useState(supplier?.contact?.phone || "");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const save = useActivosMutation((input: SupplierInput) => (isEdit ? activosApi.updateSupplier(supplier!.id, input) : activosApi.createSupplier(input)));

  const toggle = (role: SupplierRole) => setRoles((prev) => (prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]));

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const contact = contactName.trim() || email.trim() || phone.trim() ? { name: optionalText(contactName), email: optionalText(email), phone: optionalText(phone) } : null;
    // En la edición el RFC no viaja: no se edita.
    const payload: SupplierInput = { name, roles, contact, ...(isEdit ? {} : { rfc: optionalText(rfc) }) };
    const checked = validateSupplierInput(payload, { mode: isEdit ? "update" : "create", currentRfc: supplier?.rfc });
    if (!checked.ok) {
      setErrors(fieldErrors(checked.errors, lang));
      return;
    }
    setErrors({});
    try {
      await save.mutateAsync(payload);
      toast.success(t(isEdit ? "supplierForm.updated" : "supplierForm.created"));
      onClose();
    } catch (err) {
      const outcome = await handleSaveError(err, t, { navigate, openSupplier: onOpenSupplier });
      if (outcome.kind === "fields") setErrors(fieldErrors(outcome.errors, lang));
      if (outcome.kind === "redirected") onClose();
    }
  };

  return (
    <Modal
      title={t(isEdit ? "supplierForm.editTitle" : "supplierForm.createTitle")}
      onClose={onClose}
      width="w-full max-w-xl"
      content={
        <form onSubmit={onSubmit} noValidate data-testid="supplier-form">
          <FormBlock title={t("supplierForm.blocks.data")}>
            <Input label={t("fields.name")} required value={name} onChange={(e) => setName(e.target.value)} error={errors.name} />
            <Input
              label={t("fields.rfc")}
              value={rfc}
              onChange={(e) => setRfc(e.target.value)}
              disabled={isEdit}
              hint={isEdit ? t("supplierForm.rfcLocked") : t("supplierForm.rfcHint")}
              error={errors.rfc}
            />
            <fieldset>
              <legend className="mb-1 text-sm font-medium text-content">
                {t("fields.roles")} <span className="text-error">*</span>
              </legend>
              <div className="flex flex-wrap gap-4">
                {SUPPLIER_ROLES.map((role) => (
                  <label key={role} className="inline-flex items-center gap-2 text-sm text-content">
                    <input type="checkbox" checked={roles.includes(role)} onChange={() => toggle(role)} data-testid={`supplier-role-${role}`} />
                    {supplierRoleLabel(role, lang)}
                  </label>
                ))}
              </div>
              {errors.roles ? <p className="mt-1 text-xs text-error">{errors.roles}</p> : null}
            </fieldset>
          </FormBlock>
          <FormBlock title={t("supplierForm.blocks.contact")}>
            <Input label={t("fields.contactName")} value={contactName} onChange={(e) => setContactName(e.target.value)} error={errors["contact.name"]} />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Input label={t("fields.email")} type="email" value={email} onChange={(e) => setEmail(e.target.value)} error={errors["contact.email"]} />
              <Input label={t("fields.phone")} value={phone} onChange={(e) => setPhone(e.target.value)} error={errors["contact.phone"]} />
            </div>
          </FormBlock>
          <FormFooter onCancel={onClose} submitting={save.isPending} submitLabel={t(isEdit ? "common.save" : "supplierForm.create")} submitTestId="supplier-form-submit" />
        </form>
      }
    />
  );
}
